package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-229 / SH-263: the one rule of what the next publish sends to the team. The builder's Publish
 * button, its "unpublished changes" banner, the rosters list's badge and the Start to-do all ask
 * it, and [com.evoapps.evoshift.domain.usecase.PublishRosterUseCase] publishes exactly these shifts
 * (and clears the [RemovedShift]s).
 */
object RosterPublishing {
    /** The month went to the team at least once (published, or locked after it). */
    fun wasPublished(status: RosterStatus?): Boolean = status == RosterStatus.PUBLISHED || status == RosterStatus.LOCKED

    /** A shift the next publish sends: not published yet (added since), or changed since. */
    fun awaitsPublishing(shift: Shift): Boolean = !shift.isPublished || shift.hasUnpublishedChanges

    /**
     * A deleted shift leaves a [RemovedShift] when the team had it ([Shift.isPublished]): the team
     * sees it gone only after the next publish. A shift added since the last publish and deleted
     * again simply disappears.
     */
    fun leavesRemoval(shift: Shift): Boolean = shift.isPublished

    /**
     * How many changes of a published (or locked) month wait for the next publish: its [shifts]
     * added or changed since, and the published shifts [removed] from it; 0 for a month never
     * published (a draft is all unpublished: it has its own "Publish").
     */
    fun changes(status: RosterStatus?, shifts: Iterable<Shift>, removed: Collection<RemovedShift> = emptyList()): Int =
        if (wasPublished(status)) shifts.count(::awaitsPublishing) + removed.size else 0

    /**
     * SH-271: [Shift.publishedUserId] after [shift] is given to [newUserId]: on a published shift
     * taken from its person, who the team saw on it at the last publish (kept through further
     * changes until the next publish); otherwise what it was.
     */
    fun publishedAssignee(shift: Shift, newUserId: String?): String? =
        if (shift.isPublished && shift.userId != newUserId) shift.publishedUserId ?: shift.userId else shift.publishedUserId

    /**
     * SH-271: the people a republish tells: whoever has a shift added or changed since the last
     * publish, whoever such a shift was taken from ([Shift.publishedUserId]), and whoever had a
     * published shift that was deleted ([removed]). Everyone else's shifts are as they saw them.
     */
    fun changedPeople(shifts: Iterable<Shift>, removed: Collection<RemovedShift> = emptyList()): Set<String> =
        shifts.filter(::awaitsPublishing).flatMapTo(linkedSetOf()) { listOfNotNull(it.userId, it.publishedUserId) } +
            removed.mapNotNull { it.userId }

    /**
     * SH-271: the open shifts a publish announces to the team: on a republish only those not on
     * the marketplace yet (newly open since the last publish); the first publish announces all.
     */
    fun announcedOpen(republish: Boolean, open: List<Shift>): List<Shift> =
        if (republish) open.filter { it.swapRequest == null } else open
}

/**
 * SH-271: what a publish writes on the roster: its [version] (raised by every republish, which
 * e-mails the team again: functions onRosterPublished) and, on a republish, the people whose
 * shifts changed ([changedUserIds]: only they are e-mailed; null on the first publish: everyone).
 */
data class RosterPublication(val version: Int, val changedUserIds: List<String>?)

/** See [RosterPublishing.awaitsPublishing]. */
val Shift.awaitsPublishing: Boolean get() = RosterPublishing.awaitsPublishing(this)

/**
 * SH-263: a published shift deleted from its month since the last publish
 * (`rosters/{rosterId}/removed_shifts/{shiftId}`, written with the delete, cleared by publishing
 * and by putting the shift back). What it was, for "Usunięto: 3.11 Kasia 07:00–15:00".
 */
@Serializable
data class RemovedShift(
    val shiftId: String,
    val rosterId: String,
    val date: String,
    val userId: String? = null,
    val userDisplayName: String? = null,
    /** Planned start and end, ISO local date-times as on the shift. */
    val start: String = "",
    val end: String = "",
    val removedAt: String = "",
    val removedBy: String? = null
) {
    companion object {
        fun of(shift: Shift, removedAt: String, removedBy: String?) = RemovedShift(
            shiftId = shift.id,
            rosterId = shift.rosterId,
            date = shift.date,
            userId = shift.userId,
            userDisplayName = shift.userDisplayName,
            start = shift.plannedTime.start,
            end = shift.plannedTime.end,
            removedAt = removedAt,
            removedBy = removedBy
        )
    }
}
